export const BOOK = {
  title: "SELF DOUBLING",
  copyright: "©",
  subtitle: "The Truth That Dwells Within You",
  positioning: "A New Frontier in Psychodrama, Trauma and Embodied Memory",
  author: "Maria Gioia Mosca",
  period: "2011 — 2026",
  coverUrl:
    "https://customer-assets-39nsmqrw.emergentagent.net/job_description-71/artifacts/g49h0aic_IMG-20260803-WA0032.jpg",
  heroText:
    "The result of more than thirty years of work in psychodrama and fifteen years dedicated to the development of Self Doubling©.",
  emotionalLine:
    "This is the deepest embrace you can give yourself. Do you want to understand it and experience it?",
};

export const NAV_LINKS = [
  { label: "The Question", href: "#problem" },
  { label: "Self Doubling", href: "#what" },
  { label: "The Truth Within", href: "#truth" },
  { label: "The Body & Memory", href: "#roots" },
  { label: "Energy for Lasting Change", href: "#energy" },
  { label: "Maria Gioia Mosca", href: "#author" },
  { label: "Contents", href: "#book" },
];

export const ROOTS = [
  {
    title: "Trauma and Anxiety",
    body: "It is not the event that endures, but the unresolved suffering that keeps it alive — a trace that continues to act in the body, in choices, in the present, often surfacing as anxiety. Discover how this duration can be understood in Self Doubling.",
  },
  {
    title: "Trauma and Depression",
    body: "When suffering is never fully processed, it can settle into the body and mind as heaviness, numbness, or depression.",
  },
  {
    title: "Childhood Trauma",
    body: "How early experiences can leave traces that continue to shape adult emotions and relationships.",
  },
  {
    title: "Trauma and Relationships",
    body: "Recognising the relational patterns that repeat when the past has not been given a place.",
  },
  {
    title: "Trauma and the Body",
    body: "The body does not simply remember: it bears witness to the whole network of our intra- and transgenerational history. Through tension, posture, sensation and symptom, it participates in our lives and warns us when we continue to ask external systems to carry the decisions we have not yet reclaimed.",
  },
  {
    title: "Embodied Memory",
    body: "Memory is not only in the mind — it lives in the body, in gesture and in the way we hold ourselves.",
  },
  {
    title: "Transgenerational Trauma",
    body: "What remains unprocessed does not simply belong to the past: it can continue to live in the body, in posture, in relational patterns and in the silent loyalties carried across generations.",
  },
  {
    title: "Posture and Personal History",
    body: "The shape a life takes in the body — how history writes itself into the way we stand.",
  },
];

export const WHY_DIFFERENT = {
  contrasts: [
    { not: "Not the classical Double performed by an external auxiliary ego", is: "but a direct encounter with your own internal Double." },
    { not: "Not a soliloquy spoken to the room", is: "but a Double that is met, expressed and given form." },
    { not: "Not a role reversal with another person", is: "but an exchange that happens within, in a protected individual setting." },
    { not: "Not a generic inner-dialogue technique", is: "but a specific methodological process with criteria, limits and responsibility." },
  ],
  paradigm: [
    "Trauma understood as duration",
    "Embodied memory",
    "A transgenerational perspective",
    "Guided by a specifically trained therapist",
  ],
};

export const CHAPTERS = [
  { title: "The Double in Psychodrama", blurb: "Where the function of the Double begins — Moreno's stage and the mirror of the self." },
  { title: "The Birth of Self Doubling", blurb: "How fifteen years of research gave form to an original development of the Double." },
  { title: "Trauma as Duration", blurb: "Why trauma is not only an event, but something that can keep acting through time." },
  { title: "Embodied Memory", blurb: "What the body remembers when the mind has set the experience aside." },
  { title: "Trauma and the Body", blurb: "Tension, sensation and the silent language through which a history speaks." },
  { title: "Posture and Personal History", blurb: "How a life writes itself into the way we stand and hold ourselves." },
  { title: "Anxiety and Depression", blurb: "Looking beneath the surface, at the deeper roots of emotional suffering." },
  { title: "Transgenerational Processes", blurb: "What remains unspoken in one generation and continues, silently, in the next." },
  { title: "The Direct Double", blurb: "The individual setting where the protagonist meets their own Double, without external egos." },
  { title: "The Mosca Method", blurb: "The methodological path that ties research, theory and practice together." },
];

export const AUTHOR = {
  name: "Maria Gioia Mosca",
  role: "Creator of Self Doubling©",
  bio: [
    "Maria Gioia Mosca is a psychodramatist and independent researcher whose work focuses on psychodrama, embodied memory and transgenerational processes.",
    "For more than thirty years, she has devoted herself to studying the human being in their spiritual dimension.",
    "She first encountered psychodrama more than thirty years ago and developed Self Doubling© between 2011 and 2026.",
    "Her work brings together psychodrama, psychogenealogy, transgenerational therapy, embodied and bodily memory, posturology, nutrition, and the study of the relationship between the body and emotional experience.",
    "She is the originator of the Mosca Method.",
  ],
  memberships: [
    { full: "International Association for Group Psychotherapy and Group Processes", acronym: "IAGP" },
    { full: "Associazione Italiana Psicodrammatisti Moreniani", acronym: "A.I.Psi.M." },
  ],
};

export const MONICA = {
  name: "Monica Zuretti",
  role: "Master of Morenian Psychodrama",
  bio: "A profound human and professional presence in the world of Morenian psychodrama, whose teaching and lineage inform the ground from which this work has grown.",
};

export const MARQUEE_WORDS = [
  "Psychodrama",
  "Trauma",
  "Embodied Memory",
  "The Duration",
  "Self Doubling",
  "Transgenerational",
  "The Mosca Method",
];

export const TRUTH_WITHIN = [
  {
    kicker: "The Truth Within",
    body:
      "The Truth that dwells within you must be treated with profound respect, because it is the most precious part of you: the part that asks to emerge, to be recognised and, ultimately, integrated.\nEvery trauma — from the Greek τραῦμα (traûma), meaning “wound” — leaves a trace.\nIn this sense, all of us carry countless wounds within us, in the body, the mind and the soul.",
  },
  {
    kicker: "Understanding & Acceptance",
    body:
      "The states we encounter throughout life can be understood through a precise relationship:\n“Understanding is to Realisation as Acceptance is to Dissolution”.\nFirst comes Understanding, then Realisation.\nFirst comes Acceptance, then Dissolution can become possible.\nThese are processes that must be experienced in this order.",
  },
  {
    kicker: "The Mosca Method",
    body:
      "The Mosca Method was created to help and support those who feel they are in one of these states, those who genuinely wish to explore it, and those who choose to be supported all the way through that exploration.\nNot to imitate something already seen.\nNot to apply a technique.\nBut to recognise what is authentically one’s own and to give space to the truth that dwells within.",
  },
];

export const ROOTS_INTRO =
  "Emotional suffering does not live only in thoughts. It can remain stored in the body, in posture, in relational patterns and in transgenerational memory. Self Doubling© was created to accompany the person towards a deeper encounter with what has remained unspoken, unprocessed or split within the Self.";

export const FAQS = [
  {
    q: "What is Self Doubling©?",
    a: "Self Doubling© is an original development of the psychodramatic function of the Double, created by Maria Gioia Mosca. In an individual setting, the protagonist is accompanied to encounter and express directly their own internal/direct Double, without any external auxiliary ego.",
  },
  {
    q: "Is Self Doubling© a self-help method?",
    a: "No. Self Doubling© is not a self-help technique and it is not intended for autonomous practice. It requires a trained therapist, a protected setting and previous psychodramatic work.",
  },
  {
    q: "Can I practise Self Doubling© alone?",
    a: "No. It cannot be learnt by watching a video or by imitating what is done, because it is not about imitation but about authenticity. It is a guided process with specific criteria, limits and responsibility.",
  },
  {
    q: "Who created Self Doubling©?",
    a: "Self Doubling© was created by Maria Gioia Mosca, psychodramatist and independent researcher, as part of the Mosca Method, developed between 2011 and 2026.",
  },
  {
    q: "What is the difference between Doubling and Self Doubling©?",
    a: "In classical psychodrama the Double is performed by an external auxiliary ego. In Self Doubling© the protagonist meets their own internal/direct Double directly, in an individual setting, with no external auxiliary ego.",
  },
  {
    q: "What is the Direct Double / Doppio diretto?",
    a: "The Direct Double (Doppio diretto) is the internal Double that the protagonist encounters and expresses directly, rather than through another person on the scene. It is the methodological core of Self Doubling©.",
  },
  {
    q: "Who is the book for?",
    a: "For the general reader who wishes to understand the deeper roots of anxiety, depression and unresolved emotional suffering — and for professionals: psychologists, psychotherapists, psychodramatists, trauma professionals and researchers.",
  },
  {
    q: "Is it connected to psychodrama?",
    a: "Yes. Self Doubling© was born within Morenian psychodrama, but it is not a generic psychodrama technique: it is the specific methodological development created by Maria Gioia Mosca.",
  },
];

export const EDITORIAL = {
  title: "Self Doubling – The Truth That Dwells Within You",
  author: "Maria Gioia Mosca",
  isbnPaperback: "979-12-243-4022-5",
  isbnEbook: "979-12-243-4023-2",
  deposit: "Patamu — reg. no. 302610, 12 July 2026",
  contact: "contact@selfdoubling.com",
};
